import { HttpClient } from '@angular/common/http';
import { effect, inject, Injectable, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { catchError, type Observable, of } from 'rxjs';

const DEFAULT_LANG = 'en';

@Injectable({
  providedIn: 'root',
})
export class ContextualHelpService {
  readonly isOpen = signal<boolean>(false);
  readonly currentHelpKey = signal<string>('');
  readonly content = signal<string>('');
  readonly loading = signal<boolean>(false);
  readonly loadError = signal<boolean>(false);

  /**
   * Lets a page override the route-resolved help key with a more specific one
   * (e.g. score-pool varying its key by the active ScoringType). Read by
   * WrapperComponent.openHelp() at click time; cleared by the page on destroy.
   */
  readonly pageHelpKeyOverride = signal<string | null>(null);

  /**
   * How much content is currently pushed aside by the drawer in "side" mode (0
   * when closed or in "over" mode). Fixed-position elements like the nav toolbar
   * and env banner can't rely on the sidenav's own content-width shrink, since
   * that only affects elements in normal flow — they read this instead.
   */
  readonly contentInsetPx = signal(0);

  /**
   * The help key that actually matches the page currently rendered underneath the
   * drawer — kept up to date by WrapperComponent on every navigation, so the drawer
   * can tell when it's showing a doc for a *different* page (browsed via search)
   * and its spotlight links would have nothing on-screen to target.
   */
  readonly currentPageHelpKey = signal('');

  private readonly http = inject(HttpClient);
  private readonly transloco = inject(TranslocoService);
  private readonly cache = new Map<string, string>();

  constructor() {
    this.transloco.langChanges$.subscribe((lang) => {
      const helpKey = this.currentHelpKey();
      if (this.isOpen() && helpKey) {
        this.loadContent(helpKey, lang);
      }
    });

    // Self-correct if a page's override resolves asynchronously (e.g. score-pool
    // computing its key from data loaded after the drawer was already opened).
    effect(() => {
      const override = this.pageHelpKeyOverride();
      if (override && this.isOpen()) {
        this.open(override);
      }
    });
  }

  open(helpKey: string): void {
    this.currentHelpKey.set(helpKey);
    this.isOpen.set(true);
    this.loadContent(helpKey, this.transloco.getActiveLang());
  }

  close(): void {
    this.isOpen.set(false);
    // Wait a moment then clear the content so it doesn't abruptly disappear during close animation
    setTimeout(() => {
      if (!this.isOpen()) {
        this.currentHelpKey.set('');
        this.content.set('');
        this.loadError.set(false);
      }
    }, 300);
  }

  setPageHelpKey(helpKey: string | null): void {
    this.pageHelpKeyOverride.set(helpKey);
  }

  setContentInset(px: number): void {
    this.contentInsetPx.set(px);
  }

  setCurrentPageHelpKey(helpKey: string): void {
    this.currentPageHelpKey.set(helpKey);
  }

  retry(): void {
    const helpKey = this.currentHelpKey();
    if (!helpKey) return;
    this.cache.delete(`${helpKey}_${this.transloco.getActiveLang()}`);
    this.loadContent(helpKey, this.transloco.getActiveLang());
  }

  private loadContent(helpKey: string, lang: string): void {
    const cacheKey = `${helpKey}_${lang}`;
    const cached = this.cache.get(cacheKey);
    if (cached !== undefined) {
      this.content.set(cached);
      this.loadError.set(cached === '');
      return;
    }

    this.loading.set(true);
    this.loadError.set(false);
    this.fetchMarkdown(helpKey, lang).subscribe((markdown) => {
      this.cache.set(cacheKey, markdown);
      if (this.currentHelpKey() === helpKey) {
        this.content.set(markdown);
        this.loadError.set(markdown === '');
      }
      this.loading.set(false);
    });
  }

  private fetchMarkdown(helpKey: string, lang: string): Observable<string> {
    return this.http
      .get(`assets/help/${helpKey}.${lang}.md`, { responseType: 'text' })
      .pipe(
        catchError(() =>
          lang === DEFAULT_LANG
            ? of('')
            : this.http
                .get(`assets/help/${helpKey}.${DEFAULT_LANG}.md`, { responseType: 'text' })
                .pipe(catchError(() => of(''))),
        ),
      );
  }
}
